import React, { useMemo, useEffect, useContext } from 'react';
import classNames from 'classnames';
import { useData } from 'resift';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import makeStyles from '@material-ui/core/styles/makeStyles';
import usePrevious from '@sift/resift/usePrevious';
import useEntityTypes, { PERSON_ENTITY_TYPE_KEY } from 'helpers/useEntityTypes';
import useSyncInfo from 'components/DirectoryInfoBar/helpers/useSyncInfo';
import makeGetMappingsAttributes from 'fetches/makeGetMappingsAttributes';
import MappingsContext from 'helpers/MappingsContext';
import sampleDataLookup from 'components/Mappings/_helpers/sampleDataLookup';
import MappingProps, * as MappingTypes from 'types/Mapping';
import sourceTypes from 'consts/sourceTypes';
import { SyncStatus } from 'types/Source';
import { PRIMARY_MAPPING_TYPE_KEY } from 'pages/Directory/consts';

import Button from '@material-ui/core/Button';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Collapse from '@material-ui/core/Collapse';
import SourceSelector from 'components/Mappings/SourceSelector';
import ValidationStatusIndicator from 'atoms/ValidationStatusIndicator';
import AttributeSelector from '../AttributeSelector';
import CollectionMapping, { MAPPING_PRESETS } from '../CollectionMapping';

interface Props {
  mapping: MappingProps;
  rowIndex: number;
  onDeleteMapping: (index: number) => void;
  onUpdateMapping: (index: number, newMapping: any) => void;
}

function MappingsRow({ mapping, onDeleteMapping, rowIndex, onUpdateMapping }: Props) {
  const classes = useStyles();
  const entityTypes = useEntityTypes();
  const mappingContext: MappingTypes.MappingContext = useContext(MappingsContext);
  const { source, selectedDestinations } = mappingContext;
  const syncStatus = _get(source, 'syncStatus', {}) as SyncStatus;
  const syncInfo = useSyncInfo(syncStatus);
  const { syncHasRun } = syncInfo;
  const userConfig = _get(entityTypes, PERSON_ENTITY_TYPE_KEY);
  const userConfigFields = _get(userConfig, '_fields', {});
  const getMappingsAttributes = makeGetMappingsAttributes(source.id);
  const mappingAttributes = useData(getMappingsAttributes);
  const sampleUserData = _get(mappingAttributes, 'sampleData', {});
  const { source: mappingSource, destination, type, isCustom, validation } = mapping;

  const rootSourceName = (mappingSource || '').split('.')[0];
  const isCollapsed = useMemo(
    () =>
      !!MAPPING_PRESETS.find(MP => MP.key === rootSourceName) && sourceTypes.isGoogle(source.type),
    [rootSourceName, source.type],
  );
  const prevIsCollapsed = usePrevious(isCollapsed);
  const sourceName = isCollapsed ? rootSourceName : mappingSource || '';
  const attributeDisplayValue = _get(
    userConfigFields,
    `['${destination}'].displayName`,
    destination,
  );

  const _sampleData = isCollapsed
    ? sampleDataLookup(sampleUserData, mapping)
    : _get(sampleUserData, mappingSource);
  const sampleData = _sampleData || '(none)';
  const noSampleData = sampleData === '(none)';

  useEffect(() => {
    if (!isCollapsed && prevIsCollapsed) {
      const { collectionItemType, collectionItemCustomType, subSource, ...other } = mapping;

      onUpdateMapping(rowIndex, other);
    }
  }, [isCollapsed, prevIsCollapsed, mapping]);

  const handleSelectAttribute = (attribute: any) => {
    const newMapping = {
      ...mapping,
      destination: attribute.objectKey,
    };

    onUpdateMapping(rowIndex, newMapping);
  };

  const handleUpdateSource = (sourceValue: string) => {
    const newMapping = {
      ...mapping,
      source: sourceValue,
      collectionItemType: null,
      collectionItemCustomType: null,
      customProps: {
        ...mapping.customProps,
      },
    };

    onUpdateMapping(rowIndex, newMapping);
  };

  const handleUpdateMapping = (newMapping: any) => {
    onUpdateMapping(rowIndex, {
      ...mapping,
      ...newMapping,
    });
  };

  const isPrimaryMapping = type === PRIMARY_MAPPING_TYPE_KEY;
  const shouldRenderInput = isPrimaryMapping ? !syncHasRun : isCustom || !sourceName;

  return (
    <>
      <TableRow className={isCollapsed ? classes.collapsedRow : undefined} data-cy="mappings-row">
        <TableCell>
          {shouldRenderInput ? (
            <SourceSelector
              placeholderText={`Select ${isPrimaryMapping ? 'Unique Identifier' : ''} Property`}
              onChange={handleUpdateSource}
              value={mappingSource}
            />
          ) : (
            <div className={classes.ellipsisText}>{sourceName}</div>
          )}
        </TableCell>
        <TableCell
          className={classNames(classes.ellipsisText, noSampleData && classes.noSampleData)}
          title={sampleData}
        >
          {sampleData}
        </TableCell>
        {isPrimaryMapping ? (
          <TableCell colSpan={1} />
        ) : (
          <>
            <TableCell>
              <AttributeSelector
                onSelectAttribute={handleSelectAttribute}
                displayValue={attributeDisplayValue}
                selectedDestinations={selectedDestinations}
              />
            </TableCell>
            <TableCell>
              <div className={classes.actionTableCell}>
                {mapping.isCustom ? (
                  <Button onClick={() => onDeleteMapping(rowIndex)}>Delete</Button>
                ) : (
                  destination && (
                    <Button onClick={() => handleUpdateMapping({ destination: null })}>
                      Clear
                    </Button>
                  )
                )}
              </div>
            </TableCell>
          </>
        )}

        <TableCell>
          {!!mapping.destination && (
            <ValidationStatusIndicator
              status={_get(validation, 'type')}
              description={_get(validation, 'longDescriptionHtml')}
            />
          )}
        </TableCell>
      </TableRow>

      {isCollapsed && (
        <TableRow className={classes.collectionMappingRow}>
          <TableCell colSpan={5}>
            <Collapse in={isCollapsed} timeout="auto" unmountOnExit>
              <CollectionMapping
                value={sourceName}
                mapping={mapping}
                onChange={handleUpdateMapping}
              />
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

const useStyles = makeStyles(theme => {
  return {
    actionTableCell: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
    },
    collectionMappingRow: {
      backgroundColor: theme.palette.grey[100],
    },
    collapsedRow: {
      '& > *': {
        borderBottom: 'unset',
      },
    },
    ellipsisText: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    noSampleData: {
      color: theme.palette.text.disabled,
    },
  };
});

export default MappingsRow;
